import {
  CohortBaseDetail,
  CohortResponseEndDate,
  CohortResponseStartDate,
  CohortType,
} from '@platform/client/schemas';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { getMondayDate } from '../../../util/DateHelpers';
import { ApplicationBackground } from '../../ApplicationBackground';
import BackgroundImage from '../../images/Laptopmockup.svg';
import { Cohorts } from '@platform/client';

export const SignUpCohortDetails = ({
  onSubmit,
  onBack,
  isSubmitting,
}: {
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}) => {
  const { data: userCurrentCohort, isSuccess } = Cohorts.useGetCurrentUserCurrentCohort();
  return (
    isSuccess &&
    userCurrentCohort && (
      <CohortDetails
        onBack={onBack}
        cohortData={userCurrentCohort}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    )
  );
};

export const RejoinCohortDetails = ({
  cohortId,
  onSubmit,
  onBack,
  isSubmitting,
}: {
  cohortId: number;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}) => {
  const { data: rejoinCohortData, isSuccess } = Cohorts.useGetCohort(cohortId);
  return (
    isSuccess &&
    rejoinCohortData && (
      <CohortDetails
        onBack={onBack}
        cohortData={rejoinCohortData}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    )
  );
};

export const CohortDetails = ({
  cohortData,
  onSubmit,
  onBack,
  isSubmitting = false,
}: {
  cohortData: CohortBaseDetail;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting?: boolean;
}) => {
  return (
    <ApplicationBackground
      onBack={onBack}
      onSubmit={onSubmit}
      customLeftPanelContent={
        <div className='w-full h-full flex flex-col justify-end bg-secondary-brand-25 overflow-hidden'>
          <div className='relative mb-4'>
            <h2 className='absolute left-54 top-14 max-w-[25rem] text-display-md font-semibold text-gray-600'>
              Global challenges need your professional skills
            </h2>
            <img src={BackgroundImage} className='w-[34rem]' alt='laptop image' />
          </div>
        </div>
      }
      primaryCtaText={isSubmitting ? 'Submitting' : 'Submit application'}
      ctaDisabled={isSubmitting}
      hideStepper
    >
      <ApplicationPageContent
        title='Review your cohort timeline'
        description='Busy schedules and days off are expected. Stay on track with these key dates to get the most out of the experience.'
      >
        {cohortData && (
          <div className='-mt-2 flex flex-col gap-6'>
            <CohortDetailsCard
              duration={cohortData.duration}
              timeCommitment={cohortData.timeCommitment}
              format={cohortData.cohortType === CohortType.Teams ? 'Team' : 'Individual'}
            />
            <CohortTimeline
              kickOffDate={cohortData.kickoffDate}
              cohortEndDate={cohortData.endDate}
              isTeam={cohortData.cohortType === CohortType.Teams}
            />
          </div>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};

const CohortDetailsCard = ({
  duration,
  timeCommitment,
  format,
}: {
  duration?: string | null;
  timeCommitment?: string | null;
  format?: string | null;
}) => {
  if (!duration || !timeCommitment || !format) {
    return null;
  }
  return (
    <div className='w-full lg:w-[460px] py-4 px-8 bg-gray-50 border border-gray-100 flex flex-col gap-2 md:gap-0 md:flex-row justify-between rounded-xl'>
      <CohortDetail label='Duration' value={duration} />
      <CohortDetail label='Time Commitment' value={timeCommitment} />
      <CohortDetail label='Format' value={format} />
    </div>
  );
};

const CohortDetail = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className='flex flex-col gap-1'>
      <p className='text-sm text-gray-500'>{label}</p>
      <p className='text-gray-700 font-semibold'>{value}</p>
    </div>
  );
};

const CohortTimeline = ({
  kickOffDate,
  cohortEndDate,
  isTeam,
}: {
  kickOffDate: CohortResponseStartDate;
  cohortEndDate: CohortResponseEndDate;
  isTeam: boolean;
}) => {
  if (!kickOffDate || !cohortEndDate) {
    return null;
  }
  const mondayOfKickoffWeek = getMondayDate(kickOffDate);
  const kickOffItemDateStr = generateWeekOfStr(mondayOfKickoffWeek);
  const mondayOfWrapUpWeek = getMondayDate(cohortEndDate);
  const wrapUpItemDateStr = generateWeekOfStr(mondayOfWrapUpWeek);
  // derive plan and action period as 7 days after kickoff week Monday to 3 days before wrap-up week Monday
  const planAndActionStartDate = new Date(mondayOfKickoffWeek);
  planAndActionStartDate.setDate(planAndActionStartDate.getDate() + 7);
  const planAndActionEndDate = new Date(mondayOfWrapUpWeek);
  planAndActionEndDate.setDate(planAndActionEndDate.getDate() - 3);
  const planAndActionDateStr = `${planAndActionStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${planAndActionEndDate.toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
    },
  )}`;
  return (
    <div className='w-full lg:w-[460px]'>
      <h3 className='text-lg text-gray-700 mb-4 font-semibold'>Cohort Timeline</h3>
      <TimelineItem
        number={1}
        title='Kick-off'
        description={
          isTeam ?
            "Meet your cohort, get oriented, and learn about the project you'll be working on."
          : 'Meet your cohort, get oriented, then review your project recommendations and next steps.'
        }
        dateText={kickOffItemDateStr}
      />
      {!isTeam && (
        <TimelineItem
          number={2}
          title='Confirm Your Match'
          description='Explore your project recommendations and connect with organizations to find the right match.'
          dateText='Up to 3 weeks'
        />
      )}
      <TimelineItem
        number={isTeam ? 2 : 3}
        title='Plan & Action'
        description="Work with your social enterprise to plan and execute the project. You'll have regular check-ins to keep things on track."
        dateText={planAndActionDateStr}
      />
      <TimelineItem
        number={isTeam ? 3 : 4}
        title='Wrap-up & Reflection'
        description='Reflect on what you learned, celebrate with your cohort, and share the outcomes of your project.'
        dateText={wrapUpItemDateStr}
        isLast
      />
    </div>
  );
};

const TimelineItem = ({
  number,
  title,
  description,
  dateText,
  isLast = false,
}: {
  number: number;
  title: string;
  description: string;
  dateText: string;
  isLast?: boolean;
}) => {
  return (
    <div className='w-full flex gap-4 mb-6'>
      <div className='w-7'>
        <span className='w-[30px] h-[30px] rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold flex-shrink-0'>
          {number}
        </span>
        {!isLast && <div className='w-0.5 bg-gray-200 my-1 h-[27px] mx-auto rounded-lg' />}
      </div>
      <div className='flex-grow'>
        <div className='flex justify-between items-baseline mb-1'>
          <p className='text-gray-700 font-semibold'>{title}</p>
          <p className='text-xs text-brand-500'>{dateText}</p>
        </div>
        <p className='text-sm text-gray-500'>{description}</p>
      </div>
    </div>
  );
};

const generateWeekOfStr = (date: Date) => {
  const dateObj = new Date(date);
  return `Week of ${dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};
